import { useCallback, useState } from 'react';
import { GlobalBannerDynamic } from '@frontend/global-info-banner';
import { Trans, useTranslation } from '@frontend/i18n';
import { useHasPhoneSettingsAccess, usePhoneSettingsShallowStore } from '@frontend/phone-config';
import { useAppScopeStore } from '@frontend/scope';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { usePhoneOverrideShallowStoreV2 } from '@frontend/shared';
import { Text, TextLink, useAlert, useModalControl } from '@frontend/design-system';
import { useOverridesManagementPermission } from '../../userManagementPermission';
import { ActiveOverridesModal } from './active-overrides-modal';
import { NewOverrideSettingTray } from './override-settings-modal.new';
import { PhoneOverrideType } from './types';

export const OverrideNotificationRibbon = () => {
  const { t } = useTranslation('phone', { keyPrefix: 'overrides' });
  const alerts = useAlert();
  const { overridesV2 } = usePhoneOverrideShallowStoreV2('overridesV2');
  const phoneOverrides = overridesV2.phoneOverrides;
  const enabledOverridesV2 = phoneOverrides?.filter((override) => override.enabled) || [];
  const { accessibleLocationData, selectedLocationIds, groupData } = useAppScopeStore();
  const phoneAccess = useHasPhoneSettingsAccess({
    selectedLocations: selectedLocationIds?.map((id) => accessibleLocationData[id]).filter(Boolean) ?? [],
  });

  const filteredEnabledOverridesV2 = enabledOverridesV2.filter((item) => selectedLocationIds.includes(item.locationId));
  const hasOverridesManagementPermission = useOverridesManagementPermission();
  const hasNewUsersManagementFlag = filteredEnabledOverridesV2.every((override) =>
    hasOverridesManagementPermission(override?.locationId ?? '')
  );
  const locationData = accessibleLocationData[selectedLocationIds[0]];
  const selectedLocationData = locationData;

  const { navigate } = useSettingsNavigate();
  const {
    modalProps: activeOverridesModalProps,
    openModal: activeOverridesOpenModal,
    closeModal: activeOverridesCloseModal,
  } = useModalControl();
  const trayModalControl = useModalControl();
  const { setSettingsTenantLocation, settingsTenantLocation } = usePhoneSettingsShallowStore(
    'setSettingsTenantLocation',
    'settingsTenantLocation'
  );
  const hasSingleEnabledOverride = filteredEnabledOverridesV2.length === 1;
  const [overrideV2, setOverrideV2] = useState<PhoneOverrideType>();

  const getCallRouteName = () => {
    if (filteredEnabledOverridesV2?.length === 1) {
      return t('{{callRouteName}}', { callRouteName: filteredEnabledOverridesV2[0].callRouteName });
    } else if (filteredEnabledOverridesV2?.length > 1) {
      return t('{{count}} Call Routes', { count: filteredEnabledOverridesV2.length });
    }
    return null;
  };

  const OverrideLinkV2 = () => {
    return phoneAccess.hasFullPhoneAccess ? (
      <TextLink
        onClick={() =>
          navigate({
            to: '/phone/call-routes/:id',
            params: { id: filteredEnabledOverridesV2[0].callRouteId ?? '' },
          })
        }
      >
        {getCallRouteName()}
      </TextLink>
    ) : (
      <Text size='medium' as='span'>
        {getCallRouteName()}
      </Text>
    );
  };

  const message2 = hasSingleEnabledOverride ? (
    <Trans t={t}>
      Call Route <OverrideLinkV2 /> has an active override and will not direct calls normally until the override is
      removed.
    </Trans>
  ) : (
    <Trans t={t}>
      {getCallRouteName()} have active overrides and will not direct calls normally until the overrides are removed.
    </Trans>
  );

  const setTenantLocation = useCallback(() => {
    if (selectedLocationData && selectedLocationData.phoneTenantId !== settingsTenantLocation?.phoneTenantId) {
      const group =
        selectedLocationData.locationType === 'databeta'
          ? { ...selectedLocationData, children: [] }
          : groupData[selectedLocationData.groupId];
      setSettingsTenantLocation({
        id: group.id,
        phoneTenantId: group.phoneTenantId ?? '',
        locationId: group.id,
        locationType: group.locationType,
        name: group.name ?? '',
        children: group.children.map((child) => ({
          id: child.id,
          name: child.name ?? '',
          locationType: child.locationType,
          phoneTenantId: child.phoneTenantId ?? '',
          children: [],
          locationId: child.id,
          orgId: child.orgId,
          type: 'individual' as const,
        })),
        orgId: group.orgId,
        type: group.children.length > 0 ? 'group' : 'individual',
      });
    }
  }, [selectedLocationData, groupData, setSettingsTenantLocation, settingsTenantLocation]);

  const handleGoToOverridesClick = useCallback(() => {
    if (selectedLocationData && hasSingleEnabledOverride) {
      setOverrideV2(filteredEnabledOverridesV2[0]);
      trayModalControl.openModal();
    } else if (selectedLocationData) {
      setTenantLocation();
      navigate({ to: '/phone/overrides' });
      activeOverridesCloseModal();
    }
  }, [
    selectedLocationData,
    hasSingleEnabledOverride,
    filteredEnabledOverridesV2,
    setTenantLocation,
    navigate,
    activeOverridesCloseModal,
    trayModalControl,
  ]);

  const handleOpenOverridesTray = () => {
    if (selectedLocationData) {
      setTenantLocation();
      setOverrideV2(filteredEnabledOverridesV2[0]);
      trayModalControl.openModal();
    } else {
      alerts.error(t('No selected location data found.'));
    }
  };

  const handleOpenActiveOverridesModal = () => {
    setTenantLocation();
    activeOverridesOpenModal();
  };

  const [showBanner, setShowBanner] = useState(true);
  const handleDismiss = () => setShowBanner(false);

  return (
    <>
      {showBanner && !!filteredEnabledOverridesV2?.length && (
        <GlobalBannerDynamic
          id='override-notification-banner'
          type='warning'
          title={hasSingleEnabledOverride ? t('Active Override') : t('Active Overrides')}
          message={message2}
          isDismissible
          onDismiss={handleDismiss}
          action={{
            label: hasSingleEnabledOverride
              ? hasNewUsersManagementFlag
                ? t('Edit Override')
                : t('View Override')
              : hasNewUsersManagementFlag
              ? t('Manage Overrides')
              : t('View Overrides'),
            onClick: hasSingleEnabledOverride ? handleOpenOverridesTray : handleOpenActiveOverridesModal,
          }}
        />
      )}

      <ActiveOverridesModal
        modalProps={activeOverridesModalProps}
        title={t('Call Routes with Active Overrides')}
        overrides={filteredEnabledOverridesV2}
        onPrimaryActionClick={handleGoToOverridesClick}
      />

      <NewOverrideSettingTray
        override={overrideV2}
        locationId={selectedLocationIds[0]}
        modalControls={trayModalControl}
      />
    </>
  );
};
